import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["rows", "headers", "footer", "renderHeaders", "renderRows", "renderFooter", "TBody", "TFoot", "THead", "scrollable", "truncated", "bordered", "condensed", "_exteriorBorders", "colgroup", "sticky"];
import { forwardRef } from 'react';
import { useTheme } from 'foundations-theming/utils';
import { TableCell } from './elements/TableCell';
import { TableHeaderCell } from './elements/TableHeaderCell';
import { TableContext } from './TableContext';
import { TableRow } from './elements/TableRow';
import { AbstractTable } from './AbstractTable';
import { TableBody } from './elements/TableBody';
import { TableFoot } from './elements/TableFoot';
import { TableHead } from './elements/TableHead';
import { useTableCellBorderStyles, useTableStyles } from './Table.styles';
import { StyledSortTH_TH } from './internal/SortTH_TH';
import { CSS_SPECIFICITY } from '../../constants/CSSSpecificity';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/*
  Take in a list of rows, which are objects.
  Return row elements
*/
const defaultRenderRows = (rows, columnOrder) => rows.map((row, index) => /*#__PURE__*/_jsx(TableRow, {
  children: columnOrder.map(colKey => /*#__PURE__*/_jsx(TableCell, {
    children: row[colKey]
  }, colKey))
}, index));
const defaultRenderHeader = headers => /*#__PURE__*/_jsx(TableRow, {
  children: headers.map(({
    title,
    key
  }) => /*#__PURE__*/_jsx(TableHeaderCell, {
    children: title
  }, key))
});
const defaultRenderFooter = (footer, columnOrder) => {
  const columnOrderOmitFirst = columnOrder.slice(1);
  return /*#__PURE__*/_jsxs(TableRow, {
    children: [/*#__PURE__*/_jsx(TableCell, {
      children: footer.title
    }), columnOrderOmitFirst.map(colKey => /*#__PURE__*/_jsx(TableCell, {
      children: footer[colKey]
    }, colKey))]
  }, footer.key);
};
const getInitialColumnOrder = headers => headers.map(({
  key
}) => key);
export const DataTable = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      // Data objects
      rows,
      headers,
      footer,
      // Render functions
      renderHeaders = defaultRenderHeader,
      renderRows = defaultRenderRows,
      renderFooter = defaultRenderFooter,
      // Component props
      TBody = TableBody,
      TFoot = TableFoot,
      THead = TableHead,
      // Style props
      scrollable = true,
      truncated,
      bordered = true,
      condensed = true,
      _exteriorBorders = true,
      colgroup,
      sticky
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const theme = useTheme();
  const columnOrder = getInitialColumnOrder(headers);
  const {
    tableHeaderHeight,
    condensedPadding,
    tableCellVerticalAlignment
  } = useTableStyles();
  const tableCellBorderStyles = useTableCellBorderStyles({
    interiorBorders: bordered,
    isHeader: true,
    exteriorBorders: _exteriorBorders
  });
  const tableVariantStyleOverrides = Object.assign({
    fontSize: theme.typography['body-100'].fontSize,
    backgroundColor: theme.color['fill-field-default-alt'],
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: '100%',
    '& thead th': Object.assign({}, tableCellBorderStyles),
    //  This is a bit of a work around to ensure the standard table header styles are also
    // applied to the sortable header cells for consistency. Probably better ways to do this.
    [`& ${StyledSortTH_TH}`]: Object.assign({}, condensedPadding, tableCellBorderStyles, {
      fontWeight: theme.typography['heading-200'].fontWeight,
      fontSize: theme.typography['heading-25'].fontSize,
      backgroundColor: theme.color['specialty-table-header-default'],
      color: theme.color['text-core-default'],
      blockSize: tableHeaderHeight,
      verticalAlign: tableCellVerticalAlignment,
      paddingBlockEnd: theme.space['100']
    })
  }, scrollable && Object.assign({
    [CSS_SPECIFICITY.OVERRIDE]: {
      marginBlockEnd: theme.space['0']
    }
  }, truncated && {
    tableLayout: 'fixed',
    [`${TableCell}, ${TableHeaderCell}`]: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  }));
  return /*#__PURE__*/_jsx(TableContext.Provider, {
    value: {
      bordered,
      condensed,
      _exteriorBorders,
      tableVariantStyleOverrides
    },
    children: /*#__PURE__*/_jsx(AbstractTable, Object.assign({}, rest, {
      ref: ref,
      head: /*#__PURE__*/_jsx(THead, {
        sticky: sticky,
        children: renderHeaders(headers)
      }),
      body: /*#__PURE__*/_jsx(TBody, {
        children: renderRows(rows, columnOrder)
      }),
      foot: footer && /*#__PURE__*/_jsx(TFoot, {
        children: renderFooter(footer, columnOrder)
      }),
      scrollable: scrollable,
      colgroup: colgroup
    }))
  });
});
DataTable.displayName = 'DataTable';